import { EditFilled } from "@ant-design/icons";
import styles from "./TripClosing.module.scss";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  message,
  Modal,
  Pagination,
  Row,
  Space,
  Table,
} from "antd";
import type { Key } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useRecoilValue } from "recoil";
import {
  buildColumnsWithHeaderFilters,
  HeaderTable,
  TableActionButtons,
} from "../../Components";
import type { ColumnWithFilter } from "../../Components/FilterBar/FilterBar";
import { PermissionDirect } from "../../Components/PermissionDirect";
import { Setting } from "../../assets/icon";
import {
  BusinessPlanApi,
  FreightRevenueApi,
  getPermissionDirections,
  getPermissionRegions,
  OrderApi,
  RouteManagerApi,
  TruckVendorApi,
} from "../../services";
import {
  buildBusinessPlanLookupOption,
  buildCustomerLookupOption,
  buildLookupRequestParams,
  buildRouteBaseLookupOption,
  buildTruckVendorLookupOption,
  type LookupFilters,
} from "../../shared/lookupTables";
import { updateColumnDisplayState } from "../../shared/helpers/tableColumns";
import {
  formatOrderStagePeriod,
  parseOrderStagePeriod,
  serializeOrderStagePeriod,
} from "../../shared/helpers/orderStagePeriod";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";
import { buildOrderColumns } from "../Orders/columns";
import {
  applyOrderStagePeriodToRows,
  getEditingPeriodAfterBulkUpdate,
  releaseTripClosingUpdateLock,
  tryAcquireTripClosingUpdateLock,
} from "./helpers";

const tripClosingOrderOptions = {
  ORDER_V2_STATUS: [
    { label: "Chờ duyệt", value: "PENDING_APPROVAL", color: "#0082FB" },
    { label: "Đang vận chuyển", value: "SHIPPING", color: "#FF6200" },
    { label: "Đã kết thúc", value: "FINISHED", color: "#00B517" },
    { label: "Đã huỷ", value: "CANCELLED", color: "#FF0000" },
  ],
  ORDER_V2_COST_STATUS: [
    { label: "Khởi tạo", value: "INIT", color: "#0082FB" },
    {
      label: "Chờ duyệt cấp 1",
      value: "WAITING_APPROVE_L1",
      color: "#FF6200",
    },
    {
      label: "Chờ duyệt cấp 2",
      value: "WAITING_APPROVE_L2",
      color: "#FF6200",
    },
    { label: "Đã duyệt", value: "APPROVED", color: "#00B517" },
    { label: "Đã đóng", value: "CLOSED", color: "#FF0000" },
  ],
  ORDER_TRANSPORT_TYPE: [
    { label: "Nhập", value: "IMPORT" },
    { label: "Xuất", value: "EXPORT" },
    { label: "Bắc - Nam", value: "ROAD" },
    { label: "Chuyển kho", value: "TRANSFER" },
  ],
  ORDER_CONT_TYPE: [20, 40, 45, 48],
  ORDER_DELIVERY_METHOD: [
    { label: "Nguyên seal", value: "FULL_SEAL" },
    { label: "Kiểm đếm", value: "TALLY" },
  ],
  ORDER_OWNERSHIP: [
    { label: "ĐẦU TƯ", value: "INVESTMENT" },
    { label: "THUÊ KHÔ", value: "DRY_RENTAL" },
    { label: "DỊCH VỤ", value: "SERVICE" },
  ],
  ORDER_COMBINED_RATE_STATUS: [
    1,
    0.9,
    0.85,
    0.8,
    0.75,
    0.7,
    0.65,
    0.6,
    0.55,
    0.5,
    0.45,
    "CY",
  ],
};

const apiDebounceCustomer = (search: string) => {
  return FreightRevenueApi.getAllCustomer({ searchText: search }).then(
    (res: any) => res.data?.map(buildCustomerLookupOption),
  );
};

const apiDebouncePAKD = (
  search: string,
  customerId: string,
  filters: LookupFilters = {},
) => {
  return BusinessPlanApi.getAllBussinessPlan(
    {
      ...buildLookupRequestParams(search, filters, ["validFrom"]),
      state: "ACTIVE",
      customer: customerId,
    },
    { silentLoading: true },
  ).then((res: any) => res.data?.map(buildBusinessPlanLookupOption));
};

const apiDebounceRoute = (
  search: string,
  _value?: string,
  filters: LookupFilters = {},
) => {
  return RouteManagerApi.getAllRouterBase(
    buildLookupRequestParams(search, filters),
    { silentLoading: true },
  ).then((res: any) => res.data?.map(buildRouteBaseLookupOption));
};

const apiDebounceTruckVendor = (
  search: string,
  truckVendorType: "OWNER" | "CONTRACTOR" | "",
  ownershipFilter = "",
  filters: LookupFilters = {},
) => {
  return TruckVendorApi.getAllTruckVendorSearch({
    ...buildLookupRequestParams(search, filters),
    truckVendorType,
    ownership: ownershipFilter || undefined,
  }).then((res: any) => res.data?.map(buildTruckVendorLookupOption));
};

const getColumnPositionKey = (dataIndex: any) => {
  return Array.isArray(dataIndex) ? dataIndex.join(".") : String(dataIndex);
};

const TripClosing = () => {
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const canEditOrderStagePeriod = checkPermissions(
    "LOCK_DATA",
    "edit",
    features,
  );
  const [messageApi, contextHolder] = message.useMessage();
  const updateInFlightRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [savingOrderStagePeriod, setSavingOrderStagePeriod] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [bulkOrderStagePeriod, setBulkOrderStagePeriod] =
    useState<Dayjs | null>(null);
  const [editingOrderStagePeriodId, setEditingOrderStagePeriodId] = useState<
    string | null
  >(null);
  const [editingOrderStagePeriodValue, setEditingOrderStagePeriodValue] =
    useState<Dayjs | null>(null);
  const [onModalPostion, setOnModalPosition] = useState(false);
  const [permissionRegions, setPermissionRegions] = useState<
    { label: string; value: string }[]
  >([]);
  const [permissionDirections, setPermissionDirections] = useState<
    { label: string; value: string }[]
  >([]);
  const [activeColumns, setActiveColumn] = useState<ColumnWithFilter<any>[]>(
    [],
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const baseColumns = useMemo(
    () =>
      buildOrderColumns({
        activeTab: "3",
        options: tripClosingOrderOptions,
        permissionRegions,
        permissionDirections,
        apiDebounceCustomer,
        apiDebouncePAKD,
        apiDebounceRoute,
        apiDebounceTruckVendor,
      }) as ColumnWithFilter<any>[],
    [permissionDirections, permissionRegions],
  );

  useEffect(() => {
    setActiveColumn(baseColumns);
  }, [baseColumns]);

  useEffect(() => {
    let mounted = true;

    Promise.all([getPermissionRegions({}), getPermissionDirections({})]).then(
      ([regions, directions]: any) => {
        if (!mounted) return;

        setPermissionRegions(
          (regions ?? []).map((region: any) => ({
            label: region.name,
            value: region._id,
          })),
        );
        setPermissionDirections(
          (directions ?? []).map((direction: any) => ({
            label: direction.name,
            value: direction.code,
          })),
        );
      },
    );

    return () => {
      mounted = false;
    };
  }, []);

  const handleTripClosingFiltersChange = (nextFilters: any) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters(nextFilters);
  };

  const handleStartEditOrderStagePeriod = (record: any) => {
    if (updateInFlightRef.current) return;

    const currentValue = parseOrderStagePeriod(record?.orderStagePeriod);

    setEditingOrderStagePeriodId(record._id);
    setEditingOrderStagePeriodValue(currentValue);
  };

  const resetOrderStagePeriodEdit = () => {
    setEditingOrderStagePeriodId(null);
    setEditingOrderStagePeriodValue(null);
  };

  const handleCancelEditOrderStagePeriod = () => {
    if (updateInFlightRef.current) return;
    resetOrderStagePeriodEdit();
  };

  const resetBulkUpdate = () => {
    setSelectedRowKeys([]);
    setBulkOrderStagePeriod(null);
  };

  const handleCancelBulkUpdate = () => {
    if (updateInFlightRef.current) return;
    resetBulkUpdate();
  };

  const updateOrderStagePeriods = async (payload: {
    orderIds: string[];
    orderStagePeriod: string;
  }) => {
    await OrderApi.putUpdateOrderStagePeriod(payload);
    setDataSource((prev) =>
      applyOrderStagePeriodToRows(
        prev,
        payload.orderIds,
        payload.orderStagePeriod,
      ),
    );
  };

  const handleSaveOrderStagePeriod = async () => {
    if (!canEditOrderStagePeriod || !editingOrderStagePeriodId) return;

    if (!editingOrderStagePeriodValue) {
      messageApi.error("Vui lòng chọn kỳ ghi nhận");
      return;
    }

    const orderStagePeriod = serializeOrderStagePeriod(
      editingOrderStagePeriodValue,
    );
    if (!tryAcquireTripClosingUpdateLock(updateInFlightRef)) return;

    setSavingOrderStagePeriod(true);
    try {
      await updateOrderStagePeriods({
        orderIds: [editingOrderStagePeriodId],
        orderStagePeriod,
      });
      resetOrderStagePeriodEdit();
      messageApi.success("Cập nhật kỳ ghi nhận thành công");
    } catch (error: any) {
      messageApi.error(error?.message || "Cập nhật kỳ ghi nhận thất bại");
    } finally {
      releaseTripClosingUpdateLock(updateInFlightRef);
      setSavingOrderStagePeriod(false);
    }
  };

  const handleBulkUpdateOrderStagePeriod = async () => {
    if (
      !canEditOrderStagePeriod ||
      selectedRowKeys.length === 0 ||
      !bulkOrderStagePeriod
    ) {
      return;
    }

    const orderStagePeriod = serializeOrderStagePeriod(bulkOrderStagePeriod);
    const selectedOrderIds = selectedRowKeys.map(String);
    if (!tryAcquireTripClosingUpdateLock(updateInFlightRef)) return;

    setSavingOrderStagePeriod(true);
    try {
      await updateOrderStagePeriods({
        orderIds: selectedOrderIds,
        orderStagePeriod,
      });
      setEditingOrderStagePeriodValue((currentValue) =>
        getEditingPeriodAfterBulkUpdate(
          editingOrderStagePeriodId,
          selectedOrderIds,
          currentValue,
          bulkOrderStagePeriod,
        ),
      );
      resetBulkUpdate();
      messageApi.success("Cập nhật kỳ ghi nhận thành công");
    } catch (error: any) {
      messageApi.error(error?.message || "Cập nhật kỳ ghi nhận thất bại");
    } finally {
      releaseTripClosingUpdateLock(updateInFlightRef);
      setSavingOrderStagePeriod(false);
    }
  };

  const displayColumns = useMemo(
    () =>
      activeColumns.map((col): ColumnWithFilter<any> => {
        if (getColumnPositionKey(col.dataIndex) !== "orderStagePeriod") {
          return col;
        }

        return {
          ...col,
          width: col.width ?? 120,
          render: (value: any, record: any) => {
            if (editingOrderStagePeriodId === record._id) {
              return (
                <DatePicker
                  picker="month"
                  format="MM/YYYY"
                  allowClear={false}
                  disabled={savingOrderStagePeriod}
                  value={editingOrderStagePeriodValue}
                  onChange={setEditingOrderStagePeriodValue}
                />
              );
            }

            return formatOrderStagePeriod(value);
          },
        };
      }),
    [
      activeColumns,
      editingOrderStagePeriodId,
      editingOrderStagePeriodValue,
      savingOrderStagePeriod,
    ],
  );

  const columnsRender = buildColumnsWithHeaderFilters(
    [
      ...displayColumns.filter((col: any) => !col.hidden),
      {
        title: "",
        key: "action",
        width: 90,
        fixed: "right",
        render: (_: any, record: any) =>
          editingOrderStagePeriodId === record._id ? (
            <Space>
              <Button
                size="small"
                type="primary"
                loading={savingOrderStagePeriod}
                disabled={savingOrderStagePeriod}
                onClick={handleSaveOrderStagePeriod}
              >
                Lưu
              </Button>
              <Button
                size="small"
                disabled={savingOrderStagePeriod}
                onClick={handleCancelEditOrderStagePeriod}
              >
                Huỷ
              </Button>
            </Space>
          ) : (
            <TableActionButtons
              items={[
                {
                  key: "edit",
                  label: "Sửa kỳ ghi nhận",
                  icon: <EditFilled />,
                  tone: "edit",
                  disabled:
                    !canEditOrderStagePeriod || savingOrderStagePeriod,
                  onClick: () => handleStartEditOrderStagePeriod(record),
                },
              ]}
            />
          ),
      },
    ],
    filters,
    handleTripClosingFiltersChange,
    { popoverProps: { getPopupContainer: () => document.body } },
  );

  const handleOnChangePosition = (
    dataIndex: string,
    value: boolean | "left" | "right",
    type: "hidden" | "fixed",
  ) => {
    setActiveColumn((prev) =>
      updateColumnDisplayState(prev, dataIndex, value, type),
    );
  };

  const renderModalPosition = () => {
    return (
      <Modal
        open={onModalPostion}
        width="70%"
        onCancel={() => setOnModalPosition(false)}
        title="Hiển thị cột"
        footer={false}
      >
        <Row>
          <Col span={16}>Tên cột</Col>
          <Col span={4}>Hiển thị</Col>
          <Col span={4}>Cố định(trái, phải)</Col>
        </Row>
        <Divider />
        {activeColumns.map((col: any, key: number) => {
          const columnKey = getColumnPositionKey(col.dataIndex);

          return (
            <div key={key}>
              <Row>
                <Col span={16}>{col.title}</Col>
                <Col span={4}>
                  <Checkbox
                    checked={!col.hidden}
                    onChange={(e) => {
                      handleOnChangePosition(
                        columnKey,
                        e.target.checked,
                        "hidden",
                      );
                    }}
                  />
                </Col>
                <Col span={4}>
                  <Checkbox
                    checked={col.fixed === "left"}
                    onChange={(e) => {
                      handleOnChangePosition(
                        columnKey,
                        e.target.checked && "left",
                        "fixed",
                      );
                    }}
                    style={{ marginRight: "20px" }}
                  />
                  <Checkbox
                    checked={col.fixed === "right"}
                    onChange={(e) => {
                      handleOnChangePosition(
                        columnKey,
                        e.target.checked && "right",
                        "fixed",
                      );
                    }}
                  />
                </Col>
              </Row>
              <Divider />
            </div>
          );
        })}
      </Modal>
    );
  };

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const resDataOrders: any = await OrderApi.getAllOrder({
          current: pagination.current,
          pageSize: pagination.pageSize,
          status: "FINISHED",
          ...filters,
          loadingDate: undefined,
          returnDate: undefined,
          loadingDateFrom: filters.loadingDate
            ? dayjs(filters.loadingDate[0]).startOf("day").toISOString()
            : undefined,
          loadingDateTo: filters.loadingDate
            ? dayjs(filters.loadingDate[1]).endOf("day").toISOString()
            : undefined,
          returnDateFrom: filters.returnDate
            ? dayjs(filters.returnDate[0]).startOf("day").toISOString()
            : undefined,
          returnDateTo: filters.returnDate
            ? dayjs(filters.returnDate[1]).endOf("day").toISOString()
            : undefined,
        });

        if (!mounted) return;
        setDataSource(resDataOrders?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          total: resDataOrders?.totalItem || 0,
        }));
      } catch (error: any) {
        messageApi.error(
          error?.message || "Lấy danh sách chốt chuyến thất bại",
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [filters, messageApi, pagination.current, pagination.pageSize]);

  return (
    <PermissionDirect screen={["LOCK_DATA"]}>
      <div className={styles.TripClosing}>
        {contextHolder}
        <div className="container">
          <HeaderTable
            title="Chốt chuyến"
            buttons={[
              {
                children: "Cài đặt hiển thị",
                icon: <Setting />,
                onClick: () => setOnModalPosition(true),
                type: "primary",
              },
            ]}
          />
          {canEditOrderStagePeriod && selectedRowKeys.length > 0 && (
            <Space
              wrap
              style={{
                width: "100%",
                padding: 12,
                marginBottom: 12,
                background: "#f5f8ff",
                border: "1px solid #d6e4ff",
                borderRadius: 8,
              }}
            >
              <span>Đã chọn {selectedRowKeys.length} đơn</span>
              <DatePicker
                picker="month"
                format="MM/YYYY"
                placeholder="Chọn kỳ ghi nhận"
                disabled={savingOrderStagePeriod}
                value={bulkOrderStagePeriod}
                onChange={setBulkOrderStagePeriod}
              />
              <Button
                type="primary"
                disabled={!bulkOrderStagePeriod || savingOrderStagePeriod}
                loading={savingOrderStagePeriod}
                onClick={handleBulkUpdateOrderStagePeriod}
              >
                Lưu
              </Button>
              <Button
                disabled={savingOrderStagePeriod}
                onClick={handleCancelBulkUpdate}
              >
                Huỷ
              </Button>
            </Space>
          )}
          <Table
            rowKey="_id"
            columns={columnsRender}
            size="small"
            dataSource={dataSource}
            scroll={{ x: "max-content" }}
            style={{ width: "100%" }}
            pagination={false}
            loading={loading}
            rowSelection={
              canEditOrderStagePeriod
                ? {
                    fixed: "left",
                    preserveSelectedRowKeys: true,
                    selectedRowKeys,
                    getCheckboxProps: () => ({
                      disabled: savingOrderStagePeriod,
                    }),
                    onChange: (keys) => {
                      if (!updateInFlightRef.current) setSelectedRowKeys(keys);
                    },
                  }
                : undefined
            }
          />
          <Pagination
            total={pagination.total}
            current={pagination.current}
            pageSize={pagination.pageSize}
            onChange={(page, pageSize) => {
              setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: pageSize || prev.pageSize,
              }));
            }}
            showSizeChanger
            pageSizeOptions={[5, 10, 20, 50, 100]}
            align="end"
            style={{ marginTop: 16 }}
          />
        </div>
        {renderModalPosition()}
      </div>
    </PermissionDirect>
  );
};

export { TripClosing };
